/**
 * Storezone — Centralized API Client
 *
 * Wraps all HTTP calls to the FastAPI backend.
 * Automatically:
 *   - Attaches the JWT Bearer token from localStorage
 *   - Refreshes the token if a 401 is encountered
 *   - Increments/decrements the global progress indicator
 *   - Parses structured error responses
 */

import { progressStore } from '@/lib/progress-store'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string || '').replace(/\/$/, '')

if (!API_BASE_URL) {
  console.warn(
    '[Storezone] VITE_API_BASE_URL is not set in environment variables. API requests will use relative origin.',
  )
}

// ============================================================
// Error Types
// ============================================================

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Global event for authentication expiration
export const AUTH_EXPIRED_EVENT = 'sz:auth-expired'

// ============================================================
// Token Management
// ============================================================

export const TokenManager = {
  getAccessToken: () => localStorage.getItem('sz_access_token'),
  getRefreshToken: () => localStorage.getItem('sz_refresh_token'),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem('sz_access_token', access)
    localStorage.setItem('sz_refresh_token', refresh)
  },
  clearTokens: () => {
    localStorage.removeItem('sz_access_token')
    localStorage.removeItem('sz_refresh_token')
  }
}

export const TenantManager = {
  getTenantId: () => localStorage.getItem('storezone:active_tenant_id'),
  setTenantId: (id: string) => localStorage.setItem('storezone:active_tenant_id', id),
  clearTenantId: () => localStorage.removeItem('storezone:active_tenant_id')
}

// In-flight refresh promise to prevent concurrent refreshes
let refreshPromise: Promise<string | null> | null = null

async function refreshTokenFlow(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const refresh = TokenManager.getRefreshToken()
    if (!refresh) return null

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh })
      })

      if (!res.ok) {
        TokenManager.clearTokens()
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
        return null
      }

      const json = await res.json()
      if (json.success && json.data?.access_token) {
        TokenManager.setTokens(json.data.access_token, json.data.refresh_token)
        return json.data.access_token
      }
      return null
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// ============================================================
// Base fetch wrapper
// ============================================================

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestInit,
  isRetry = false
): Promise<T> {
  const token = TokenManager.getAccessToken()
  const tenantId = TenantManager.getTenantId()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  // Track request in global progress indicator
  progressStore.increment()

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: { ...headers, ...(options?.headers as Record<string, string>) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    })

    // Parse response body
    let json: unknown
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      json = await response.json()
    }

    if (!response.ok) {
      if (response.status === 401 && !isRetry) {
        // Try to refresh token
        const newToken = await refreshTokenFlow()
        if (newToken) {
          return request<T>(method, path, body, options, true)
        } else {
          window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
        }
      }

      // Try to parse standardized APIErrorResponse
      const errorBody = json as {
        success?: boolean
        error?: { status?: number; message?: string; details?: unknown }
        detail?: string | any[] // FastAPI detail format
      } | null

      let message = `HTTP ${response.status}`
      if (errorBody?.error?.message) {
        message = errorBody.error.message
      } else if (errorBody?.detail) {
        if (typeof errorBody.detail === 'string') message = errorBody.detail
        else if (Array.isArray(errorBody.detail)) message = errorBody.detail.map(d => d.msg).join(', ')
      }

      throw new ApiError(
        response.status,
        message,
        errorBody?.error?.details || errorBody?.detail,
      )
    }

    // Success response unwrapping
    const successBody = json as { success?: boolean; data?: unknown; meta?: unknown } | null
    if (successBody && successBody.success !== undefined) {
       return successBody.data as T
    }

    return json as T
  } finally {
    progressStore.decrement()
  }
}

// ============================================================
// API Client Interface
// ============================================================

export const apiClient = {
  get<T>(path: string, options?: RequestInit): Promise<T> {
    return request<T>('GET', path, undefined, options)
  },

  post<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>('POST', path, body, options)
  },

  put<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>('PUT', path, body, options)
  },

  patch<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return request<T>('PATCH', path, body, options)
  },

  delete<T>(path: string, options?: RequestInit): Promise<T> {
    return request<T>('DELETE', path, undefined, options)
  },
}

export async function apiUpload<T>(
  path: string,
  formData: FormData,
  isRetry = false
): Promise<T> {
  const token = TokenManager.getAccessToken()
  const tenantId = TenantManager.getTenantId()

  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  
  progressStore.increment()
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    })
    
    let json: unknown
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      json = await response.json()
    }
    
    if (!response.ok) {
      if (response.status === 401 && !isRetry) {
        const newToken = await refreshTokenFlow()
        if (newToken) {
          return apiUpload<T>(path, formData, true)
        } else {
          window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
        }
      }
      throw new ApiError(response.status, `HTTP ${response.status}`)
    }
    
    const successBody = json as { success?: boolean; data?: unknown } | null
    if (successBody && successBody.success !== undefined) {
      return successBody.data as T
    }
    return json as T
  } finally {
    progressStore.decrement()
  }
}
